<template>
	<UIModal v-model:is-modal-open="isModalOpen" max-width="700px">
		<template #content>
			<Transition name="modal-toast">
				<div v-if="toast.visible" class="modal-toast">
					{{ toast.message }}
				</div>
			</Transition>
			<div v-if="isSended" class="modal-success">
				<UIHeading level="4" class="modal-success-title">
					{{ $t('pages.publishers.modal.success.title') }}
				</UIHeading>
				<p class="modal-success-text">
					{{ $t('pages.publishers.modal.success.text') }}
				</p>
				<button type="button" class="modal-success-btn common-btn" @click="publisherModal.closeModal">
					{{ $t('basics.ok') }}
				</button>
			</div>
			<div v-else class="modal-content">
				<UIHeading level="4" class="modal-header-title">
					{{ $t('pages.publishers.modal.title') }}
				</UIHeading>
				<div class="modal-header">
					<div class="modal-header-description">
						<p class="modal-header-description-title">
							{{ $t('pages.publishers.modal.description.title') }}
						</p>
						<p class="modal-header-description-text">
							{{ $t('pages.publishers.modal.description.text') }}
						</p>
					</div>
					<img
						src="/images/pages/publishers/modals/calendar.png"
						alt="calendar"
						class="modal-header-img" />
				</div>
				<form class="modal-form" @submit.prevent="handleSumbitForm">
					<input
						v-model="formData.fullname"
						type="text"
						required
						:placeholder="$t('pages.publishers.modal.form.name')"
						class="modal-form-input" />
					<VueTelInput
						v-model="formData.phone"
						mode="national"
						:auto-format="false"
						:valid-characters-only="true"
						:auto-default-country="true"
						:dropdown-options="{
							showDialCodeInList: true,
							showDialCodeInSelection: true,
							showFlags: true,
						}"
						:input-options="{
							placeholder: $t('pages.publishers.modal.form.phone'),
							showDialCode: false,
							required: true,
							maxlength: maxNationalPhoneLength,
						}"
						class="modal-form-input-phone"
						@country-changed="handleCountryChanged"
						@on-input="handlePhoneInput" />
					<input
						v-model="formData.email"
						type="email"
						required
						:placeholder="$t('pages.publishers.modal.form.email')"
						class="modal-form-input" />
					<textarea
						v-model="formData.question"
						rows="4"
						type="text"
						:placeholder="$t('pages.publishers.modal.form.question')"
						class="modal-form-input" />
					<div class="modal-form-footer">
						<div class="modal-form-agreement">
							<input id="agreement" type="checkbox" required />
							<label for="agreement">
								{{ $t('pages.publishers.modal.form.agreement.text') }}
								<a
									:href="`/files/privacy-policy-${locale === 'en' ? 'ru' : locale}.pdf`"
									target="_blank"
									class="modal-form-agreement-link">
									{{ $t('pages.publishers.modal.form.agreement.link') }}
								</a>
							</label>
						</div>
						<button type="submit" class="modal-form-btn" :disabled="isSubmitting">
							<Icon
								v-if="isSubmitting"
								name="svg-spinners:3-dots-fade"
								size="20"
								class="modal-form-btn-icon" />
							<span v-else>{{ $t('basics.send') }}</span>
						</button>
					</div>
				</form>
			</div>
		</template>
	</UIModal>
</template>

<script setup lang="ts">
	import startEarningMutation from '@/graphql/startEarning.gql'
	import { useReCaptcha } from 'vue-recaptcha-v3'
	import type { CountryObject, PhoneObject } from 'vue-tel-input'
	import { VueTelInput } from 'vue-tel-input'
	import 'vue-tel-input/vue-tel-input.css'

	const publisherModal = usePublisherModal()

	const { locale, t } = useI18n()

	const isSended = ref(false)
	const isSubmitting = ref(false)
	const MAX_PHONE_LENGTH = 16
	const selectedDialCode = ref('998')
	let toastTimeout: ReturnType<typeof setTimeout> | null = null
	const toast = reactive({
		visible: false,
		message: '',
	})

	const showToast = (message: string) => {
		toast.message = message
		toast.visible = true
		if (toastTimeout) {
			clearTimeout(toastTimeout)
		}
		toastTimeout = setTimeout(() => {
			toast.visible = false
		}, 4000)
	}

	onBeforeUnmount(() => {
		if (toastTimeout) {
			clearTimeout(toastTimeout)
		}
	})
	const { public: publicRuntimeConfig } = useRuntimeConfig()
	const isRecaptchaEnabled = String(publicRuntimeConfig.recaptchaEnabled).toLowerCase() !== 'false'
	const recaptchaSiteKey = String(publicRuntimeConfig.recaptchaSiteKey || '')
	const isRecaptchaAvailable = isRecaptchaEnabled && !!recaptchaSiteKey
	const recaptchaInstance = isRecaptchaAvailable ? useReCaptcha() : null

	const _recaptcha = async () => {
		if (!isRecaptchaAvailable || !recaptchaInstance) {
			return 'recaptcha-disabled'
		}

		await recaptchaInstance.recaptchaLoaded()
		const token = await recaptchaInstance.executeRecaptcha('yourActionHere')
		return token || 'recaptcha-disabled'
	}

	const maxNationalPhoneLength = computed(() => {
		return Math.max(1, MAX_PHONE_LENGTH - selectedDialCode.value.length)
	})

	const handleCountryChanged = (country: CountryObject) => {
		selectedDialCode.value = String(country?.dialCode || '').replace(/\D/g, '')
		formData.phone = formData.phone.replace(/\D/g, '').slice(0, maxNationalPhoneLength.value)
	}

	const handlePhoneInput = (_value: string, phoneObject: PhoneObject) => {
		const rawNational = String(phoneObject?.nationalNumber || formData.phone || '').replace(/\D/g, '')
		formData.phone = rawNational.slice(0, maxNationalPhoneLength.value)
	}

	const isModalOpen = defineModel('isModalOpen', {
		type: Boolean,
		default: false,
	})

	const formData = reactive({
		fullname: '',
		phone: '',
		email: '',
		question: '',
	})
	const resetForm = () => {
		formData.fullname = ''
		formData.phone = ''
		formData.email = ''
		formData.question = ''
	}

	const { mutate } = useMutation(startEarningMutation, { fetchPolicy: 'no-cache' })

	const handleSumbitForm = async () => {
		if (isSubmitting.value) {
			return
		}

		const normalizedNationalPhone = formData.phone
			.replace(/\D/g, '')
			.slice(0, maxNationalPhoneLength.value)
		const dialCodeDigits = selectedDialCode.value.replace(/\D/g, '')
		const normalizedPhone = `${dialCodeDigits}${normalizedNationalPhone}`.slice(0, MAX_PHONE_LENGTH)

		if (!normalizedNationalPhone || !dialCodeDigits) {
			showToast('Please enter a valid phone number')
			return
		}

		formData.phone = normalizedNationalPhone
		isSubmitting.value = true

		try {
			const response = await mutate({
				...formData,
				phone: normalizedPhone,
				// recaptchaResponse: await _recaptcha(),
			})

			const payload = response?.data?.start_earning
			if (!payload?.success) {
				const errorMessage =
					payload?.errors?.join('\n') || payload?.message || t('basics.error') || 'Request failed'
				showToast(errorMessage)
				return
			}

			resetForm()
			isSended.value = true
		} catch (error) {
			showToast((error as Error)?.message || t('basics.error') || 'Request failed')
		} finally {
			isSubmitting.value = false
		}
	}
</script>

<style scoped lang="scss">
	.modal {
		&-toast {
			position: sticky;
			top: 8px;
			z-index: 30;
			padding: 12px 16px;
			margin: 16px 32px 0;
			font-size: 14px;
			color: var(--white);
			background-color: #d93025;
			border-radius: 10px;
			@media (width <= 575.98px) {
				margin: 16px 16px 0;
			}
		}
		&-header {
			display: flex;
			align-items: flex-start;
			gap: 16px;
			margin-top: 37px;

			&-title {
				text-transform: uppercase;
			}
			&-description {
				&-title {
					font-weight: 600;
				}
				&-text {
					margin-top: 16px;
					font-size: 14px;
				}
			}
			&-img {
				max-width: 90px;
				max-height: 90px;
				width: 100%;
				height: 100%;
				object-fit: contain;
			}
		}
		&-content {
			padding: 32px 32px 0;
			@media (width <= 575.98px) {
				padding: 16px 16px 0;
			}
		}
		&-form {
			display: flex;
			flex-direction: column;
			gap: 16px;
			width: 100%;
			padding-top: 16px;
			&-input {
				padding: 16px;
				resize: vertical;
				background-color: var(--gray200);
				border-radius: 10px;
			}
			&-input-phone {
				position: relative;
				background-color: var(--gray200);
				border-radius: 10px;
				border: none;
				outline: none;
				box-shadow: none;
				:deep(.vue-tel-input) {
					position: relative;
					border: none;
					background-color: transparent;
					box-shadow: none;
				}

				:deep(.vti__dropdown) {
					position: static;
					background-color: transparent;
				}
				:deep(.vti__selection) {
					font-size: 16px;
					font-weight: 400;
					color: var(--gray600);
				}
				:deep(.vti__country-code) {
					font-size: 16px;
					font-weight: 400;
					color: var(--gray600);
				}
				:deep(.vti__dropdown:hover) {
					background-color: var(--gray300);
				}
				:deep(.vti__dropdown-list) {
					overscroll-behavior: none;
					top: calc(100% + 4px);
					left: 0;
					right: 0;
					min-width: 100%;
					width: 100%;
					margin: 0;
					box-sizing: border-box;
					z-index: 20;
				}
				:deep(.vti__input) {
					padding: 16px;
					font: inherit;
					background-color: transparent;
				}
				// :deep(.vti__input::placeholder) {
				// 	// color: var(--gray600);
				// }
			}
			&-agreement {
				display: flex;
				gap: 8px;
				align-items: center;
				margin-top: 16px;
				font-size: 14px;
				color: var(--gray600);
				input[type='checkbox'] {
					width: 16px;
					height: 16px;
					accent-color: var(--primary);
					cursor: pointer;
				}
				&-link {
					color: var(--primary);
					text-decoration: underline;
					cursor: pointer;
					&:hover {
						color: var(--primary-hover);
					}
				}
			}
			&-btn {
				display: inline-flex;
				gap: 8px;
				align-items: center;
				justify-content: center;
				padding: 16px;
				color: var(--black);
				background-color: var(--primary);
				border-radius: 10px;
				&:disabled {
					opacity: 0.7;
					cursor: not-allowed;
				}
				&:hover {
					background-color: var(--primary-hover);
				}
				&-icon {
					animation: modal-spin 0.9s linear infinite;
				}
			}
			&-footer {
				position: sticky;
				bottom: 0;
				background-color: var(--white);
				display: flex;
				flex-direction: column;
				gap: 16px;
				padding-bottom: 32px;
				@media (width <= 575.98px) {
					padding-bottom: 16px;
				}
			}
		}
		&-success {
			margin-top: 16px;
			font-size: 18px;
			// color: var(--green);
			text-align: center;
			display: flex;
			flex-direction: column;
			align-items: center;
			gap: 16px;
			padding-bottom: 16px;
			// @media (width <= 575.98px) {
			// 	padding-bottom: 16px;
			// }
		}
	}

	.modal-toast-enter-active,
	.modal-toast-leave-active {
		transition: opacity 0.2s ease;
	}

	.modal-toast-enter-from,
	.modal-toast-leave-to {
		opacity: 0;
	}

	@keyframes modal-spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
</style>
